import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { Text } from "@athena/ui";
import { fullName, type ClientRecord } from "../helpers/client";

export interface LeftOutGroup {
    /** Why they are left out, as its heading: "Do not contact", "Unsubscribed". */
    label: string;
    clients: ClientRecord[];
}

interface LeftOutListProps {
    groups: LeftOutGroup[];
}

/** A group longer than this starts collapsed, so a long one doesn't bury the rest of the page. */
const OPEN_UP_TO = 5;

const byName = (clients: ClientRecord[]) => [...clients].sort((a, b) => fullName(a).localeCompare(fullName(b)));

/**
 * The alumni a broadcast leaves out on its own, grouped by why, so the sender can see who is
 * missing. Read-only on purpose: do not contact and an unsubscribe are requests to honor, so there
 * is nothing to check. A do not contact reason stays on the profile — it can be that they passed away.
 *
 * Each group is a disclosure headed by its reason and count, so how many are left out reads at a
 * glance and the names are one click away. Nothing scrolls inside it: a list cut off by a scroll box
 * hides that there is more.
 */
export function LeftOutList({ groups }: LeftOutListProps) {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col gap-3">
            {groups
                .filter((group) => group.clients.length > 0)
                .map((group) => (
                    <section key={group.label} aria-label={group.label}>
                        <details className="group" open={group.clients.length <= OPEN_UP_TO}>
                            <summary className="hover:bg-muted -mx-2 flex cursor-pointer list-none items-center gap-1.5 rounded-md px-2 py-1 [&::-webkit-details-marker]:hidden">
                                <ChevronRight
                                    aria-hidden
                                    className="text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-90"
                                />
                                <Text as="span" size="xs" weight="semibold" muted>
                                    {group.label} · {group.clients.length}
                                </Text>
                            </summary>
                            <ul className="mt-2 flex flex-col gap-2 pl-5">
                                {byName(group.clients).map((client) => (
                                    <li key={client.id} className="min-w-0">
                                        <Text as="span" size="sm" weight="medium" className="block truncate">
                                            {fullName(client)}
                                        </Text>
                                        <Text as="span" size="xs" muted className="block truncate">
                                            {client.email?.trim() || t("admin.alumni.broadcast.noEmailOnFile")}
                                        </Text>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    </section>
                ))}
        </div>
    );
}
